import {
  ControlOutlined,
  CrownOutlined,
  DashboardOutlined,
  FilePdfOutlined,
  FileProtectOutlined,
  MenuOutlined,
  PercentageOutlined,
  SafetyOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { Brand } from '@shared/ui/Brand';
import { UserMenu } from '@shared/ui/UserMenu';
import { Button, Drawer, Grid, Layout, Menu } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';

const { Sider, Header, Content } = Layout;

/**
 * Главный layout админки: навигация + шапка + основной контент.
 *
 * На десктопе (≥lg) — сворачиваемый левый сайдбар; на мобильном — бургер
 * в шапке, открывающий выдвижное меню (Drawer), контент занимает всю ширину.
 * Активный пункт определяется по `location.pathname`.
 * Пункты меню гейтятся по правам через `accessControlProvider` (action `list`
 * соответствующего ресурса) — без права пункт скрыт.
 * `Outlet` рендерит вложенные роуты (страницы фич).
 */
export function AdminLayout() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: canUsers } = useCan({ resource: 'users', action: 'list' });
  const { data: canAdmins } = useCan({ resource: 'admins', action: 'list' });
  const { data: canRoles } = useCan({ resource: 'roles', action: 'list' });
  const { data: canSettings } = useCan({ resource: 'settings', action: 'list' });
  const { data: canFeeTariffs } = useCan({ resource: 'fee-tariffs', action: 'list' });
  const { data: canAgreements } = useCan({ resource: 'agreements', action: 'list' });

  const items = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: t('menu.dashboard'),
    },
    ...(canUsers?.can
      ? [
          {
            key: '/users',
            icon: <TeamOutlined />,
            label: t('menu.users'),
          },
        ]
      : []),
    ...(canAdmins?.can
      ? [
          {
            key: '/admins',
            icon: <CrownOutlined />,
            label: t('menu.admins'),
          },
        ]
      : []),
    ...(canAgreements?.can
      ? [
          {
            key: '/agreements',
            icon: <FileProtectOutlined />,
            label: t('menu.agreements'),
          },
        ]
      : []),
    ...(canRoles?.can
      ? [
          {
            key: '/rbac',
            icon: <SafetyOutlined />,
            label: t('menu.rbac'),
          },
        ]
      : []),
    {
      key: '/documents',
      icon: <FilePdfOutlined />,
      label: t('menu.documents'),
    },
    ...(canFeeTariffs?.can
      ? [
          {
            key: '/fee-tariffs',
            icon: <PercentageOutlined />,
            label: t('menu.feeTariffs'),
          },
        ]
      : []),
    ...(canSettings?.can
      ? [
          {
            key: '/settings',
            icon: <ControlOutlined />,
            label: t('menu.settings'),
          },
        ]
      : []),
  ];

  const selectedKey =
    items
      .map((i) => i.key)
      .sort((a, b) => b.length - a.length)
      .find((k) => (k === '/' ? location.pathname === '/' : location.pathname.startsWith(k))) ??
    '/';

  const menu = (
    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      items={items}
      onClick={({ key }) => {
        setMenuOpen(false);
        void navigate(key);
      }}
      style={{ borderInlineEnd: 'none', paddingBlock: 12 }}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={232}
          collapsedWidth={64}
          theme="light"
          style={{
            borderInlineEnd: '1px solid #E2E8F0',
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <div style={{ padding: '0 16px', borderBlockEnd: '1px solid #E2E8F0' }}>
            <Brand collapsed={collapsed} />
          </div>
          {menu}
        </Sider>
      )}

      {isMobile && (
        <Drawer
          placement="left"
          width={260}
          open={menuOpen}
          onClose={() => {
            setMenuOpen(false);
          }}
          closable={false}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ padding: '0 16px', borderBlockEnd: '1px solid #E2E8F0' }}>
            <Brand collapsed={false} />
          </div>
          {menu}
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            background: '#FFFFFF',
            borderBlockEnd: '1px solid #E2E8F0',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            paddingInline: isMobile ? 12 : 24,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              aria-label={t('menu.open')}
              onClick={() => {
                setMenuOpen(true);
              }}
            />
          ) : (
            <span />
          )}
          <UserMenu />
        </Header>

        <Content
          style={{ padding: isMobile ? '16px 12px' : '24px 32px', background: '#F8FAFC' }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
